/**
 * Reveal/hide the sticky bottom CTA bar.
 *
 * On a casino review it appears once the user scrolls past the review header.
 * On a landing page (no review header — e.g. the brand-hijacking template) it
 * appears after ~500px of scroll so the offer stays in reach without covering
 * the hero.
 *
 * Adds .has-cwp-sticky-cta to <body> as a marker for pages carrying the bar.
 * It no longer drives a body padding-bottom: the footer's ::after spacer,
 * sized from --cwp-bottom-bar-h (initBottomBarHeight in main.js), keeps the
 * footer's last content clear of the bar at its real height.
 */
export const cwpStickyCta = {
	init() {
		const bar = document.querySelector('[data-sticky-cta]');
		if (!bar) return;

		document.body.classList.add('has-cwp-sticky-cta');

		const header = document.querySelector('.cwp-review-header');

		// Landing page (no review header) OR no IntersectionObserver: reveal on
		// a simple scroll threshold.
		if (!header || typeof IntersectionObserver === 'undefined') {
			const onScroll = () => {
				bar.classList.toggle('is-visible', window.scrollY > 500);
			};
			onScroll();
			window.addEventListener('scroll', onScroll, { passive: true });
			return;
		}

		// Casino review: a 1px sentinel just after the header. When it passes
		// the top upward, we've scrolled past the header. That top is the
		// review tabs' sticky top, not the window's: the header's group sticks
		// and parks the sentinel just above the tabs, under the desktop menu
		// row (frontend.scss, "Review tabs"). It moves at the 900px breakpoint,
		// with keyboard focus in the logo row and when a campaign strip pinned
		// above the header shows or goes (main.js, --cwp-top-strip-h), so
		// observe again then.
		const sentinel = document.createElement('div');
		sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;';
		header.parentNode.insertBefore(sentinel, header.nextSibling);

		const tabs = document.querySelector('.cwp-review-tabs');
		let observer = null;
		let line = null;
		const observe = () => {
			const top = tabs ? parseFloat(getComputedStyle(tabs).top) || 0 : 0;
			if (top === line) return;
			line = top;
			observer?.disconnect();
			observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						const past = !entry.isIntersecting && entry.boundingClientRect.top < entry.rootBounds.top;
						bar.classList.toggle('is-visible', past);
					});
				},
				{ threshold: 0, rootMargin: `${-top}px 0px 0px 0px` }
			);
			observer.observe(sentinel);
		};
		observe();
		window.addEventListener('resize', observe, { passive: true });
		document.addEventListener('focusin', () => requestAnimationFrame(observe));
		document.addEventListener('focusout', () => requestAnimationFrame(observe));
		const strip = document.querySelector('.cwp-top-strip');
		if (strip) new ResizeObserver(() => requestAnimationFrame(observe)).observe(strip);
	},
};
